<script setup>
const { t } = useI18n()
import { useI18n } from 'vue-i18n'

import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from './stores/store.js';
import { useAuthStore } from './stores/auth.js';
import { usePermissions } from './composables/usePermissions.js';
import { useToast } from './composables/useToast.js';
import { usePostHog } from './composables/usePostHog.js';
import { useLocale } from './composables/useLocale.js';
import { ensureOfflineExecutors } from './composables/useOfflineOps.js';
import { getSystemVersion } from './api/api.js';
import TopBar from './components/layout/TopBar.vue';
import ContextBar from './components/layout/ContextBar.vue';
import BottomNav from './components/layout/BottomNav.vue';
import DesktopSidebar from './components/layout/DesktopSidebar.vue';
import ToastContainer from './components/feedback/ToastContainer.vue';
import PageLoadingBar from './components/layout/PageLoadingBar.vue';

const store     = useStore();
const authStore = useAuthStore();
const route     = useRoute();
const router    = useRouter();
const toast     = useToast();
const posthog   = usePostHog();
const { isAdmin } = usePermissions();
const { loadOverridesAndLocale } = useLocale();

// Prevent flash of authenticated content before the auth guard has resolved
const routerReady = ref(false);
router.isReady().then(() => { routerReady.value = true; });

// Wire the toast container instance into the global useToast composable
const toastRef = ref(null);
onMounted(() => {
  if (toastRef.value) toast.setInstance(toastRef.value);
  ensureOfflineExecutors();
});

// ── Pre-load app data once the user is authenticated with a resolved tenant ───
function tryIdentifyPostHog() {
  if (authStore.isAuthenticated && authStore.currentTenant && authStore.user?.id) {
    posthog.identify(authStore.user.id, {
      email: authStore.user.email,
      tenant: authStore.currentTenant.slug,
    });
  }
}

watch(
  () => [authStore.isAuthenticated, authStore.currentTenant],
  ([authenticated]) => {
    if (authenticated && authStore.currentTenant) {
      store.fetchFrontpage?.();
      store.fetchCategories?.();
      store.fetchLocations?.();
      store.fetchWarehouses?.();
      loadOverridesAndLocale();
      // Identify the user in PostHog (no-op when consent not yet given)
      tryIdentifyPostHog();
    } else if (!authenticated) {
      posthog.reset();
    }
  },
  { immediate: true },
);

// ── Re-identify in PostHog if consent was granted while already logged in ────
watch(posthog.consentStatus, (status) => {
  if (status === 'accepted') tryIdentifyPostHog();
});

// ── Global error toast ───────────────────────────────────────────────────────
const onGlobalError = (e) => {
  const msg = e?.detail?.message || t('common.error');
  toast.error(msg);
};

// ── Step-up authentication redirect ──────────────────────────────────────────
const onStepUpRequired = () => {
  router.push({ name: 'StepUp' });
};

onMounted(() => {
  window.addEventListener('global-error', onGlobalError);
  window.addEventListener('auth:step-up-required', onStepUpRequired);
});
onBeforeUnmount(() => {
  window.removeEventListener('global-error', onGlobalError);
  window.removeEventListener('auth:step-up-required', onStepUpRequired);
});

// Version info — only fetched for super_admin users
const appVersion = __APP_VERSION__;
const versionInfo = ref(null);

watch(
  isAdmin,
  (admin) => {
    if (admin && authStore.permissions?.system_roles?.includes('super_admin')) {
      getSystemVersion().then(res => {
        versionInfo.value = res;
      }).catch(() => {});
    } else {
      versionInfo.value = null;
    }
  },
  { immediate: true },
);

// Convenience flag: hide chrome on auth screens or when not authenticated
const isAuthRoute = () =>
  !authStore.isAuthenticated ||
  route.meta?.hideChrome ||
  ['/login', '/tenant', '/tenant/oidc', '/auth/callback', '/auth/step-up'].some(p => route.path.startsWith(p));

// Hide navigation chrome for users with no location access (role resolved, no locations, not admin)
const isNoAccess = computed(() =>
  store.locationsFetched &&
  !store.isLoadingLocations &&
  authStore.permissions !== null &&
  store.locations.length === 0 &&
  !isAdmin.value
);
</script>

<template>
  <div class="defaultBackground text-white min-h-screen">
    <a href="#main-content" class="skip-link">{{ $t('ux.skipToContent') }}</a>
    <template v-if="routerReady">
      <PageLoadingBar />
      <DesktopSidebar v-if="!isAuthRoute() && !isNoAccess" />
      <TopBar v-if="!isAuthRoute() && !isNoAccess" />
      <ContextBar v-if="!isAuthRoute() && !isNoAccess" />

      <main id="main-content" tabindex="-1" :class="{ 'md:pl-60': !isAuthRoute() && !isNoAccess }">
        <router-view />
      </main>

      <!-- Footer branding — only on authenticated screens -->
      <footer v-if="!isAuthRoute() && !isNoAccess" class="flex flex-col items-center pb-6 pt-2 opacity-25">
        <img src="/eqsys-white.svg" alt="eqSys" style="height:18px; width:51px; object-fit:contain;" />
        <div v-if="versionInfo" class="mt-1 text-center" style="font-size:0.6rem; line-height:1.4; color:rgba(255,255,255,0.6);">
          <div>frontend v{{ appVersion }} &middot; api {{ versionInfo.version }}</div>
          <div>Django {{ versionInfo.django_version }} &middot; Python {{ versionInfo.python_version.split(' ')[0] }}</div>
        </div>
      </footer>

      <BottomNav v-if="!isAuthRoute() && !isNoAccess" />
    </template>

    <ToastContainer ref="toastRef" />
  </div>
</template>
